/**
 * Site Settings Loader
 *
 * Loads site-wide settings from CMS-managed YAML files.
 * These settings are editable through the Decap CMS dashboard.
 */

import { parse } from 'yaml';

export interface GeneralSettings {
  company_name: string;
  address: string;
  city: string;
  state: string;
  zip: string;
  phone: string;
  email: string;
  website: string;
}

export interface MenuItem {
  label: string;
  url: string;
  order: number;
  description?: string;
  icon?: string;
  children?: MenuItem[];
}

export interface NavigationSettings {
  menu_items: MenuItem[];
}

export interface SEOSettings {
  default_title?: string;
  default_description?: string;
  default_og_image?: string;
  default_twitter_image?: string;
  twitter_handle?: string;
  facebook_app_id?: string;
}

export interface ScriptsSettings {
  head_scripts?: string;
  footer_scripts?: string;
  robots_txt?: string;
}

// Cache for settings to avoid repeated fetches
let settingsCache: {
  general?: GeneralSettings;
  navigation?: NavigationSettings;
  seo?: SEOSettings;
  scripts?: ScriptsSettings;
} = {};

/**
 * Load general settings (address, phone, email, etc.)
 */
export async function loadGeneralSettings(): Promise<GeneralSettings | null> {
  if (settingsCache.general) {
    return settingsCache.general;
  }

  try {
    const response = await fetch('/content/settings/general.yml');
    if (!response.ok) {
      console.warn('Failed to load general settings, using defaults');
      return getDefaultGeneralSettings();
    }

    const text = await response.text();
    // Layer the file over the defaults: a malformed file parses to {} and a
    // partial one lacks fields, and either must not blank the footer.
    const parsed = Object.fromEntries(
      Object.entries(parseYAML(text) as Record<string, unknown>).filter(([, v]) => v != null)
    );
    const settings = { ...getDefaultGeneralSettings(), ...parsed } as GeneralSettings;
    settingsCache.general = settings;
    return settings;
  } catch (error) {
    console.error('Error loading general settings:', error);
    return getDefaultGeneralSettings();
  }
}

/**
 * Load navigation settings (menu structure)
 */
export async function loadNavigationSettings(): Promise<NavigationSettings | null> {
  if (settingsCache.navigation) {
    return settingsCache.navigation;
  }

  try {
    const response = await fetch('/content/settings/navigation.yml');
    if (!response.ok) {
      console.warn('Failed to load navigation settings, using defaults');
      return getDefaultNavigationSettings();
    }

    const text = await response.text();
    const settings = parseYAML(text) as NavigationSettings;
    // Guard: if YAML parse returned empty/invalid (e.g. Vercel catch-all returned HTML),
    // fall back to hard-coded defaults so the nav never renders empty.
    if (
      !settings?.menu_items ||
      !Array.isArray(settings.menu_items) ||
      settings.menu_items.length === 0
    ) {
      console.warn('Navigation YAML parsed to empty — falling back to defaults');
      return getDefaultNavigationSettings();
    }
    settingsCache.navigation = settings;
    return settings;
  } catch (error) {
    console.error('Error loading navigation settings:', error);
    return getDefaultNavigationSettings();
  }
}

/**
 * Load SEO settings
 */
export async function loadSEOSettings(): Promise<SEOSettings | null> {
  if (settingsCache.seo) {
    return settingsCache.seo;
  }

  try {
    const response = await fetch('/content/settings/seo.yml');
    if (!response.ok) {
      return {};
    }

    const text = await response.text();
    const settings = parseYAML(text) as SEOSettings;
    settingsCache.seo = settings;
    return settings;
  } catch (error) {
    console.error('Error loading SEO settings:', error);
    return {};
  }
}

/**
 * Load scripts settings
 */
export async function loadScriptsSettings(): Promise<ScriptsSettings | null> {
  if (settingsCache.scripts) {
    return settingsCache.scripts;
  }

  try {
    const response = await fetch('/content/settings/scripts.yml');
    if (!response.ok) {
      return {};
    }

    const text = await response.text();
    const settings = parseYAML(text) as ScriptsSettings;
    settingsCache.scripts = settings;
    return settings;
  } catch (error) {
    console.error('Error loading scripts settings:', error);
    return {};
  }
}

/**
 * Get full address string
 */
export async function getFullAddress(): Promise<string> {
  const settings = await loadGeneralSettings();
  if (!settings) return '';
  return `${settings.address}, ${settings.city}, ${settings.state} ${settings.zip}`;
}

/**
 * Clear settings cache (useful after CMS updates)
 */
export function clearSettingsCache(): void {
  settingsCache = {};
}

/**
 * Parse a CMS settings file. Uses a real YAML parser: the hand-rolled one this
 * replaced kept missing spec cases (blank lines before nested lists, block
 * scalar chomping and indentation), each of which silently corrupted a menu,
 * robots.txt, or injected script. Returns {} for anything that is not a
 * mapping, such as the SPA's index.html served in place of a missing file.
 */
export function parseYAML(yaml: string): unknown {
  try {
    const parsed: unknown = parse(yaml);
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed)
      ? (parsed as Record<string, unknown>)
      : {};
  } catch {
    return {};
  }
}

/**
 * Default settings fallbacks
 */
function getDefaultGeneralSettings(): GeneralSettings {
  return {
    company_name: 'Rooflyzer OS',
    address: '30 N Gould St #55327',
    city: 'Sheridan',
    state: 'WY',
    zip: '82801',
    phone: '(370) 289-8830',
    email: 'support@rooflyzer.pro',
    website: 'https://rooflyzer.pro',
  };
}

/** Return the fallback menu when navigation settings cannot be loaded. */
export function getDefaultNavigationSettings(): NavigationSettings {
  return {
    menu_items: [
      {
        label: 'Platform',
        url: '/features',
        order: 1,
        children: [
          {
            label: 'Lead Tracking & CRM',
            url: '/features/lead-tracking',
            description: 'Pipeline from first knock to paid invoice',
            icon: 'Users',
            order: 1,
          },
          {
            label: 'Canvassing & Territory',
            url: '/features/canvassing-widget',
            description: 'Routes, knock lists, offline field mode',
            icon: 'Map',
            order: 2,
          },
          {
            label: 'Estimating',
            url: '/features/estimating',
            description: 'Measurements and multi-tier pricing',
            icon: 'Ruler',
            order: 3,
          },
          {
            label: 'Proposal Builder',
            url: '/features/proposals',
            description: 'Branded proposals and e-signatures',
            icon: 'FileCheck',
            order: 4,
          },
          {
            label: 'Marketing Automation',
            url: '/features/marketing-automation',
            description: 'Nurture journeys for storm and retail',
            icon: 'Sparkles',
            order: 5,
          },
          {
            label: 'Invoicing & Payments',
            url: '/features/invoicing',
            description: 'Collect faster with branded invoices',
            icon: 'CreditCard',
            order: 6,
          },
          {
            label: 'Project Management',
            url: '/features/project-management',
            description: 'Jobs, crews, and production stages',
            icon: 'ClipboardList',
            order: 7,
          },
          {
            label: 'Storm Intelligence',
            url: '/features/storm-canvassing',
            description: 'Hail windows and neighborhood targeting',
            icon: 'CloudLightning',
            order: 8,
          },
          {
            label: 'All Platform Modules',
            url: '/features',
            description: 'See the full operating system',
            icon: 'LayoutGrid',
            order: 9,
          },
        ],
      },
      {
        label: 'Solutions',
        url: '/for-roofers',
        order: 2,
        children: [
          {
            label: 'Residential Retail',
            url: '/for-roofers',
            description: 'Owner-operators running a tight crew',
            icon: 'Home',
            order: 1,
          },
          {
            label: 'Storm Restoration',
            url: '/features/storm-canvassing',
            description: 'Insurance stages from inspection to RCV',
            icon: 'CloudRain',
            order: 2,
          },
          {
            label: 'Multi-location Teams',
            url: '/enterprise',
            description: 'Shared playbooks across branches',
            icon: 'Building2',
            order: 3,
          },
          {
            label: 'Agencies & Partners',
            url: '/agencies',
            description: 'Run multiple contractor brands',
            icon: 'Handshake',
            order: 4,
          },
        ],
      },
      {
        label: 'Enterprise',
        url: '/enterprise',
        order: 3,
        children: [
          {
            label: 'Enterprise Overview',
            url: '/enterprise',
            description: 'Procurement, rollout, and scale',
            icon: 'Building',
            order: 1,
          },
          {
            label: 'Security',
            url: '/security',
            description: 'Tenant isolation, encryption, role-based access',
            icon: 'Shield',
            order: 2,
          },
          {
            label: 'Data Freedom',
            url: '/data-freedom',
            description: 'Your data leaves with you',
            icon: 'Unlock',
            order: 3,
          },
          {
            label: 'Integrations',
            url: '/integrations',
            description: 'Connect the tools you already run',
            icon: 'Plug',
            order: 4,
          },
        ],
      },
      {
        label: 'Resources',
        url: '/blog',
        order: 4,
        children: [
          {
            label: 'Compare Alternatives',
            url: '/compare',
            description: 'JobNimbus, AccuLynx, Leap, Roofr',
            icon: 'GitCompare',
            order: 1,
          },
          {
            label: 'Blog',
            url: '/blog',
            description: 'Playbooks for roofing operators',
            icon: 'FileText',
            order: 2,
          },
          {
            label: 'FAQ',
            url: '/faq',
            description: 'Pricing, onboarding, and support',
            icon: 'HelpCircle',
            order: 3,
          },
        ],
      },
      { label: 'Pricing', url: '/pricing', order: 5 },
      { label: 'Demo', url: '/demo', order: 6 },
    ],
  };
}
